import { Button, Checkbox, Divider, HStack, Icon, Text, VStack } from '@chakra-ui/react';

import SectionContainer from '~/containers/SectionContainer';

import DisplayInput from '~/components/DisplayInput';
import SettlementSection from '~/components/SettlementSection';
import { CompanyTaxForm } from '../Cart/components';

import {
  CouponInfoBar,
  NotCreatedOrFailOrderNotice,
  OrderItemList,
  OrderProgressSection,
  PaymentTypeBar,
} from './components';

import {
  IoCashOutline,
  IoGolfOutline,
  IoLocationOutline,
  IoRestaurantOutline,
  IoTimeOutline,
} from 'react-icons/io5';

import { format } from 'date-fns';

import * as fp from 'lodash/fp';

import transformCouponDiscountString from '~/utils/transformCouponDiscountString';
import transformDateRangeToString from '~/utils/transformDateRangeToString';

import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { CartType, TaxType } from '~/types/cart';
import { PaymentType } from '~/types/common';
import { OrderStatus, PlatformOrderWithId, failOrderStatus } from '~/types/order';
import { weekLabel } from '~/types/utilities';

type OrderDetailProps = {
  order: PlatformOrderWithId;
  onOpenConcatModal: () => void;
  onOpenPaymentDetailModal: (paymentType: PaymentType) => void;
};

export default function OrderDetail({
  order,
  onOpenConcatModal,
  onOpenPaymentDetailModal,
}: OrderDetailProps) {
  const {
    type,
    createdAt,
    pickMealAt,
    pickMealEnd,
    orders,
    taxType,
    taxInfo,
    paymentType,
    total,
    subtotal,
    status,
    statusRecord,
    reasonsForCancellation,
    paymentInfo,
    pickMealPoint,
    discount,
    isGroupOrder,
    couponDetail,
  } = order;
  const { enterpriseGroupBuyId = '' } = useParams();

  const createAtString = (() => {
    const createAtDate = createdAt.toDate();

    const dateString = format(createAtDate, 'MM/dd');
    const weekString = weekLabel[createAtDate.getDay()].label;
    const timeString = format(createAtDate, 'HH:mm');
    return `${dateString}(${weekString}) ${timeString}`;
  })();

  const isDisplayTaxInfo = useMemo(() => {
    if (!!enterpriseGroupBuyId) return taxInfo?.invoice?.length > 0 && taxInfo?.title?.length > 0;
    return fp.eq(taxType, TaxType.COMPANY);
  }, [enterpriseGroupBuyId, taxType, taxInfo]);

  return (
    <VStack align="stretch" spacing={4}>
      {/** order progress and status section */}
      <SectionContainer
        title="餐點進度"
        icon={<Icon as={IoGolfOutline} boxSize="1.5rem" color="secondary" />}
      >
        {fp.eq(status, OrderStatus.UNPAID) || failOrderStatus.includes(status) ? (
          <NotCreatedOrFailOrderNotice
            status={status}
            reasonsForCancellation={reasonsForCancellation}
            paymentInfo={paymentInfo}
            total={total}
          />
        ) : (
          <OrderProgressSection orderType={type} status={status} statusRecord={statusRecord} />
        )}
      </SectionContainer>

      {/** return meal box section */}
      {/* <ReturnNoticeSection /> */}

      <SectionContainer
        title="取餐地點"
        icon={<Icon as={IoLocationOutline} boxSize="1.5rem" color="secondary" />}
      >
        <DisplayInput value={pickMealPoint?.name || ''} fontWeight="bold" letterSpacing="0.1em" />
      </SectionContainer>

      {/** pick meal time section */}
      <SectionContainer
        title="取餐時間區間"
        icon={<Icon as={IoTimeOutline} boxSize="1.5rem" color="secondary" />}
      >
        <VStack align="stretch">
          <DisplayInput
            value={transformDateRangeToString(pickMealAt.toDate(), pickMealEnd?.toDate() || null)}
            fontWeight="bold"
            letterSpacing="0.1em"
          />
          <Text fontSize="xs">下單時間：{createAtString}</Text>
        </VStack>
      </SectionContainer>

      <Divider opacity="1" borderColor="gray.200" />

      {/** order items section */}
      <SectionContainer
        title="餐點"
        icon={<Icon as={IoRestaurantOutline} boxSize="1.5rem" color="secondary" />}
      >
        <OrderItemList
          orders={orders}
          cartType={isGroupOrder ? CartType.CART_GROUP : CartType.CART_PERSONAL}
        />
      </SectionContainer>

      {/** tax type section */}
      {isDisplayTaxInfo && (
        <>
          <HStack align="center" spacing="8px" pointerEvents="none">
            <Checkbox size="lg" isChecked={true} />
            <Text fontSize="sm" fontWeight={400}>
              需要統編
            </Text>
          </HStack>
          <CompanyTaxForm taxInfo={taxInfo} isReadOnly={true} />
          <Text fontSize="xs">
            weserve 點餐系統僅提供發票資訊給店家，實際開立發票方式由店家決定。
          </Text>
        </>
      )}

      <Divider opacity="1" borderColor="gray.200" />

      {/** payment type section */}
      <SectionContainer
        title="付款方式"
        icon={<Icon as={IoCashOutline} boxSize="1.5rem" color="secondary" />}
      >
        <PaymentTypeBar
          paymentType={paymentType}
          onOpenPaymentDetailModal={() => onOpenPaymentDetailModal(paymentType)}
        />
      </SectionContainer>

      <Divider opacity="1" borderColor="gray.200" />

      {couponDetail && (
        <CouponInfoBar
          value={transformCouponDiscountString(couponDetail.type, couponDetail.condition)}
        />
      )}

      {/** settlement section */}
      <SettlementSection
        displayList={[{ label: '餐點總金額', price: subtotal }].concat(
          fp.gt(discount, 0) ? [{ label: '使用優惠券', price: -discount }] : [],
        )}
        total={total}
      />
      {/* paymentType === PaymentType.TZ_WALLET => 只有聯絡平台的按鈕，不能取消訂單 */}
      <Button
        variant="outline"
        isFullWidth
        borderColor="gray.400"
        color="fontColor"
        fontSize="sm"
        fontWeight="normal"
        _hover={{}}
        onClick={onOpenConcatModal}
      >
        聯絡平台
      </Button>
    </VStack>
  );
}
